import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getEmotions } from '../services/emotionService'; // Import emotion service
import { useAdminMenu } from '../context/AdminMenuContext';
import '../styles/Nav.scss';

import navIcon1 from '../assets/nav_1.svg';
import navIcon2 from '../assets/nav_2.svg';
import navIcon3 from '../assets/nav_3.svg';
import navIcon4 from '../assets/nav_4.svg';
import navIcon5 from '../assets/nav_5.svg';
import navIcon6 from '../assets/nav_6.svg';
import navIcon6w from '../assets/nav_6_w.svg';
import navIcon7 from '../assets/nav_7.svg';
import navIcon7w from '../assets/nav_7_w.svg';
import navIcon8 from '../assets/nav_8.svg';
import navIcon8w from '../assets/nav_8_w.svg';
import navIcon9 from '../assets/nav_9.svg';
import navIcon9w from '../assets/nav_9_w.svg';
import navIcon10 from '../assets/nav_10.svg';
import navIcon10w from '../assets/nav_10_w.svg';
import navIcon11 from '../assets/nav_11.svg';
import navIcon11w from '../assets/nav_11_w.svg';
import navIcon12 from '../assets/nav_12.svg';
import navIcon12w from '../assets/nav_12_w.svg';
import navIcon13 from '../assets/nav_13.svg';
import navIcon13w from '../assets/nav_13_w.svg';
import navIcon14 from '../assets/nav_14.svg';
import navIcon14w from '../assets/nav_14_w.svg';
import navIcon15 from '../assets/nav_15.svg';
import navIcon15w from '../assets/nav_15_w.svg';
import navIcon16 from '../assets/nav_16.svg';
import navIcon16w from '../assets/nav_16_w.svg';

interface NavItem {
  path?: string;
  label: string;
  icon: string;
  icon_w: string;
  children?: NavItem[];
}

const Nav: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [averageTemp, setAverageTemp] = useState<string | null>(null);
  
  // Admin 메뉴 상태 가져오기
  const { isMobileMenuOpen } = useAdminMenu();


  const desktopNavItems: NavItem[] = [
    { path: '/', label: '메인홈', icon: navIcon1, icon_w: '' },
    { path: '/thermometer', label: '마음온도', icon: navIcon2, icon_w: '' },
    {
      label: '리본상담',
      icon: navIcon3,
      icon_w: '',
      children: [
        { path: '/counseling/new', label: '새로운 상담 시작', icon: '', icon_w: '' },
        { path: '/counseling/list', label: '내 상담 목록', icon: '', icon_w: '' },
      ],
    },
    { path: '/community', label: '커뮤니티', icon: navIcon4, icon_w: '' },
    { path: '/profile', label: '프로필', icon: navIcon5, icon_w: '' },
  ];

  if (user?.role === 'admin') {
    desktopNavItems.push({ path: '/admin', label: '관리자페이지', icon: navIcon16, icon_w: '' });
  }

  const mobileNavItems: NavItem[] = [
    { path: '/', label: '홈', icon: navIcon1, icon_w: '' },
    { path: '/thermometer', label: '마음온도', icon: navIcon2, icon_w: '' },
    { path: '/counseling/new', label: '리본상담', icon: navIcon3, icon_w: '' },
    { path: '/community', label: '커뮤니티', icon: navIcon4, icon_w: '' },
    { path: '/more', label: '더보기', icon: navIcon5, icon_w: '' },
  ];

  const adminNavItems: NavItem[] = [
    { path: '/admin', label: '대시보드', icon: navIcon6, icon_w: navIcon6w },
    { path: '/admin/users', label: '사용자 관리', icon: navIcon7, icon_w: navIcon7w },
    { path: '/admin/counselors', label: '상담사 관리', icon: navIcon8, icon_w: navIcon8w },
    {
      path: '/admin/counselor-registrations',
      label: '상담사 등록 관리',
      icon: navIcon8,
      icon_w: navIcon8w,
    },
    { path: '/admin/daily-quotes', label: '하루 한마디', icon: navIcon10, icon_w: navIcon10w },
    {
      path: '/admin/counseling-history',
      label: '상담내역 관리',
      icon: navIcon12,
      icon_w: navIcon12w,
    },
    { path: '/admin/community', label: '커뮤니티 관리', icon: navIcon13, icon_w: navIcon13w },
    { path: '/admin/comment', label: '댓글 관리', icon: navIcon9, icon_w: navIcon9w },
    {
      path: '/admin/test-results',
      label: '심리검사 결과 관리',
      icon: navIcon11,
      icon_w: navIcon11w,
    },
    {
      path: '/admin/big-five-results',
      label: 'Big-Five 결과 관리',
      icon: navIcon14,
      icon_w: navIcon14w,
    },
    { path: '/admin/logs', label: '시스템 로그', icon: navIcon16, icon_w: navIcon16w },
    { path: '/admin/activity-logs', label: '사용자 활동 로그', icon: navIcon16, icon_w: navIcon16w },
    { path: '/', label: '사용자 페이지', icon: navIcon15, icon_w: navIcon15w },
  ];

  const menuItems = location.pathname.includes('/admin') ? adminNavItems : desktopNavItems;

  useEffect(() => {
    const fetchAverageTemp = async () => {
      try {
        const emotions = await getEmotions();
        if (emotions.length > 0) {
          const sum = emotions.reduce((acc, emotion) => acc + emotion.value, 0);
          const avg = sum / emotions.length;
          setAverageTemp(avg.toFixed(1));
        } else {
          setAverageTemp('0');
        }
      } catch (error) {
        console.error('Failed to fetch average temperature', error);
        setAverageTemp('Error');
      }
    };

    fetchAverageTemp();
  }, []);

  useEffect(() => {
    const activeParent = menuItems.find(isParentActive);
    if (activeParent) {
      setOpenSubMenu(activeParent.label);
    }
  }, [location.pathname]);

  const { closeMobileMenu } = useAdminMenu();

  const handleParentClick = (item: NavItem) => {
    if (item.children) {
      setOpenSubMenu(openSubMenu === item.label ? null : item.label);
    }
    if (item.path) {
      navigate(item.path);
      // 관리자 페이지에서 모바일 메뉴가 열려있으면 닫기
      if (location.pathname.includes('/admin') && isMobileMenuOpen) {
        closeMobileMenu();
      }
    }
  };

  const handleMenuClick = () => {
    // 관리자 페이지에서 모바일 메뉴가 열려있으면 닫기
    if (location.pathname.includes('/admin') && isMobileMenuOpen) {
      closeMobileMenu();
    }
  };

  const isParentActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some(child => location.pathname.startsWith(child.path!));
  };

  const renderDesktopMenu = () => {
    return menuItems.map((item, index) => {
      const isSubmenuParent = !!item.children;
      const adminCheck = location.pathname.includes('/admin');
      let isActive = location.pathname === item.path || isParentActive(item);
      if (!isActive && adminCheck && item.path && item.path !== '/admin' && item.path !== '/') {
        if (location.pathname.startsWith(item.path)) {
          isActive = true;
        }
      }

      const icon = adminCheck && isActive ? item.icon_w : item.icon;
      if (isSubmenuParent) {
        return (
          <li
            key={index}
            className={`nav__item has-submenu ${adminCheck && isActive ? 'adminActive' : ''} ${isActive ? 'active' : ''}`}
          >
            <div className="nav__item-content" onClick={() => handleParentClick(item)}>
              <img src={icon} alt={item.label} className="nav__icon" />
              <span>{item.label}</span>
            </div>
            {openSubMenu === item.label && (
              <ul className="nav__sub-list">
                {item.children!.map((child, childIndex) => (
                  <li
                    key={childIndex}
                    className={`nav__sub-item ${location.pathname === child.path ? 'active' : ''}`}
                  >
                    <Link to={child.path!} onClick={handleMenuClick}>{child.label}</Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      }
      // 관리자 페이지 링크는 새창으로 열기 (일반 페이지에서)
      const isAdminPageLink = item.path === '/admin' && !location.pathname.includes('/admin');
      // 관리자 페이지에서 사용자 페이지 링크는 새창으로 열기
      const isUserPageLink = item.path === '/' && location.pathname.includes('/admin');
      
      return (
        <li
          key={index}
          className={`nav__item ${adminCheck && isActive ? 'adminActive' : ''} ${isActive ? 'active' : ''}`}
        >
          {isAdminPageLink || isUserPageLink ? (
            <a 
              href={item.path} 
              className={`nav__item-content ${isUserPageLink || isAdminPageLink ? 'link' : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMenuClick}
            >
              <img src={icon} alt={item.label} className="nav__icon" />
              <span>{item.label}</span>
            </a>
          ) : (
            <Link to={item.path!} className="nav__item-content" onClick={handleMenuClick}>
              <img src={icon} alt={item.label} className="nav__icon" />
              <span>{item.label}</span>
            </Link>
          )}
        </li>
      );
    });
  };

  const renderMobileMenu = () => {
    return mobileNavItems.map((item, index) => (
      <li key={index} className={`nav__item ${location.pathname === item.path ? 'active' : ''}`}>
        <Link to={item.path!} className="nav__item-content">
          <img src={item.icon} alt={item.label} className="nav__icon" />
          <span>{item.label}</span>
        </Link>
      </li>
    ));
  };

  return (
    <nav className="nav">
      {/* 모바일 메뉴 오버레이 */}
      {location.pathname.includes('/admin') && isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={closeMobileMenu}
          aria-label="메뉴 닫기"
        />
      )}
      <div className={`${location.pathname.includes('/admin') ? "desktop-nav admin-tesktop-nav" :"desktop-nav"} ${location.pathname.includes('/admin') && isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        {!location.pathname.includes('/admin') && (
          <>
            <Link to="/profile" className="nav__user-info">
              <span className="nav__user-id">{user?.userId}</span>
              <span className="nav__user-name">{user?.fullName}</span>
            </Link>
            <Link to="/thermometer" className="nav__temp-display">
              <span>나의 마음온도</span>
              <span className="nav__temp-badge">평균</span>
              <span className="nav__temp-value">{averageTemp}°</span>
            </Link>
          </>
        )}

        <ul className="nav__list">{renderDesktopMenu()}</ul>
      </div>
      {!location.pathname.includes('/admin') && (
        <ul className="nav__list mobile-nav">{renderMobileMenu()}</ul>
      )}
    </nav>
  );
};
export default Nav;
